/**
 * Admin events list.
 *
 * Search (debounced), phase filter, client filter, date range filter, a sort
 * picker, sortable Date/Client columns (the picker and the headers are one
 * piece of state), phase badges,
 * pagination, row/bulk delete, and row
 * click-through to the workspace route (`/events/:id`). Data comes from the Go
 * service via events-api (`GET` / `DELETE /api/admin/events`); phase
 * labels/colours live in admin/src/lib/booking-phase.ts.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError } from "../../../shared/api-client";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { AdminPageHeader } from "../components/ui/AdminPageHeader";
import { DateInputNZ } from "../components/ui/DateInputNZ";
import { ChevronLeftIcon, ChevronRightIcon } from "../components/ui/icons";
import {
  ResponsiveTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui/ResponsiveTable";
import { Select } from "../components/ui/Select";
import { Tooltip } from "../components/ui/Tooltip";
import {
  listEvents,
  deleteEvents,
  isSortField,
  type Booking,
  type Client,
  type SortField,
  type SortOrder,
} from "../lib/events-api";
import {
  PHASE_COLORS,
  PHASE_LABELS,
  PHASE_ORDER,
  isPhaseFilter,
  type PhaseFilter,
} from "../lib/booking-phase";
import { formatDateRange, formatTimeRange } from "../lib/format-utils";
import { formatNZD } from "@shared/nz-money";
import { cx } from "@shared/cx";
import badge from "../lib/status-badge.module.css";
import page from "./page.module.css";
import styles from "./events.module.css";

const ITEMS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The list opens chronologically: an Operator arrives to see what is coming,
 * and `upcoming` puts the nearest Event on top with the past behind it.
 */
const DEFAULT_SORT: SortField = "upcoming";
const DEFAULT_ORDER: SortOrder = "desc";

/**
 * Sort toggle: same field flips order, new field starts ascending.
 *
 * `upcoming` reads as event_date ascending here, because that is what it looks
 * like in the header — otherwise the first click on Date would "flip" to the
 * arrow already showing.
 */
function nextSort(
  current: { field: SortField; order: SortOrder },
  field: SortField,
): { field: SortField; order: SortOrder } {
  const from: { field: SortField; order: SortOrder } =
    current.field === "upcoming"
      ? { field: "event_date", order: "asc" }
      : current;
  if (from.field === field) {
    return { field, order: from.order === "asc" ? "desc" : "asc" };
  }
  return { field, order: "asc" };
}

/**
 * The sort picker's options, in `field:order` form (`upcoming` ignores order,
 * so it is its own value).
 *
 * It exists because the column headers are not reachable at every width: below
 * `md` the events table stacks into cards and the whole `<thead>` is hidden, so
 * on a phone the arrows are not merely small — they are absent. The picker is
 * the sort control that always exists; the headers are the desktop shortcut to
 * the same state. It is also the only way back to `upcoming`, which no column
 * can express.
 */
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "upcoming", label: "Upcoming first" },
  { value: "event_date:asc", label: "Date (earliest first)" },
  { value: "event_date:desc", label: "Date (latest first)" },
  { value: "client_name:asc", label: "Client (A–Z)" },
  { value: "client_name:desc", label: "Client (Z–A)" },
];

export default function EventsPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters live in the URL, not component state: a filtered list is a place
  // in the app, so it is linkable, bookmarkable, and survives Back. Only the
  // search box keeps a local draft, so typing can debounce before it commits.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPhase = searchParams.get("phase") ?? "";
  const phaseFilter: PhaseFilter = isPhaseFilter(rawPhase) ? rawPhase : "";
  const clientFilter = searchParams.get("client") ?? "";
  const dateFrom = searchParams.get("from") ?? "";
  const dateTo = searchParams.get("to") ?? "";
  const debouncedSearch = searchParams.get("q") ?? "";
  const currentPage = Math.max(1, Number(searchParams.get("page")) || 1);

  // The sort is a filter like any other, so it lives in the URL too — a list
  // read chronologically is a different place from the same list read as a
  // log, and both must survive a share and a Back.
  const rawSort = searchParams.get("sort") ?? "";
  const sortField: SortField = isSortField(rawSort) ? rawSort : DEFAULT_SORT;
  const sortOrder: SortOrder =
    searchParams.get("order") === "asc" ? "asc" : DEFAULT_ORDER;
  const sort = useMemo(
    () => ({ field: sortField, order: sortOrder }),
    [sortField, sortOrder],
  );
  // The picker and the column headers are one piece of state, not two, so they
  // can never contradict each other on screen: clicking a header moves the
  // picker, and choosing from the picker moves the arrows.
  const chronological = sortField === "upcoming";

  const [search, setSearch] = useState(debouncedSearch);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /**
   * Commit a filter change to the URL. Any filter change drops back to page 1
   * — page numbers do not survive a change of what is being paged through.
   * `replace` keeps filter tweaks out of the Back history; only the page the
   * operator arrived on is a history entry worth returning to.
   */
  const setFilter = useCallback(
    (patch: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === "") next.delete(key);
            else next.set(key, value);
          }
          if (!("page" in patch)) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [deleting, setDeleting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // Delete failures render inside the confirm modal, at the interaction point
  // — the page banner is for page-scoped failures (list load) only.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Events — Rental Admin";
  }, []);

  // Debounce the free-text search so each keystroke doesn't push a URL.
  useEffect(() => {
    if (search === debouncedSearch) return;
    const t = setTimeout(() => setFilter({ q: search }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search, debouncedSearch, setFilter]);

  // A filter change invalidates the selection (the rows behind it are gone).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [debouncedSearch, phaseFilter, clientFilter, dateFrom, dateTo, sort]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    void (async () => {
      try {
        const res = await listEvents(
          {
            search: debouncedSearch,
            phase: phaseFilter,
            client: clientFilter,
            dateFrom,
            dateTo,
            sort: sort.field,
            order: sort.order,
            limit: ITEMS_PER_PAGE,
            offset: (currentPage - 1) * ITEMS_PER_PAGE,
          },
          ac.signal,
        );
        if (ac.signal.aborted) return;
        setBookings(res.bookings);
        setTotal(res.total);
        // The client dropdown is populated from the first successful load and
        // kept stable thereafter — filtering by client must not shrink its own
        // option list.
        setClients((prev) => (prev.length === 0 ? res.clients : prev));
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (ac.signal.aborted) return;
        setError(
          err instanceof ApiError || err instanceof Error
            ? err.message
            : String(err),
        );
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [
    debouncedSearch,
    phaseFilter,
    clientFilter,
    dateFrom,
    dateTo,
    sort,
    currentPage,
    refreshKey,
  ]);

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const LATE_PHASES = new Set([
    "approved",
    "deposit",
    "crew",
    "event",
    "balance",
    "payout",
    "done",
  ]);

  const latePhaseSelected = useMemo(
    () =>
      bookings.filter((b) => selectedIds.has(b.id) && LATE_PHASES.has(b.phase)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookings, selectedIds],
  );

  const deleteModalMessage = `Permanently delete ${selectedIds.size} event${selectedIds.size === 1 ? "" : "s"}? This cannot be undone.`;
  const deleteModalWarning =
    latePhaseSelected.length > 0
      ? `${latePhaseSelected.length} of the selected event${latePhaseSelected.length === 1 ? " has" : "s have"} real financial commitments (${latePhaseSelected.map((b) => `${b.event_name ?? b.quote_number} (${PHASE_LABELS[b.phase]})`).join(", ")}). All associated data (deposits, invoices, contractor assignments) will be permanently removed.`
      : undefined;

  const handleConfirmDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      await deleteEvents(ids);
      setDeleteModalOpen(false);
      setDeleteError(null);
      setSelectedIds(new Set());
      // refreshKey forces the fetch effect to re-run; the filters themselves
      // are unchanged, so nothing else would invalidate the list.
      setRefreshKey((k) => k + 1);
    } catch (err) {
      // Keep the modal open so the error shows beside the action that failed.
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  const startItem = total === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, total);

  /**
   * Every sortable header carries an arrow at rest, muted, showing the
   * direction a click would apply — a header that only sprouts an arrow once it
   * is already the active sort never tells an Operator it was sortable in the
   * first place. The active column's arrow is the dark one.
   *
   * `upcoming` runs the Date column forwards, so Date reads as ↑ under it.
   */
  const sortIndicator = (field: SortField) => {
    const active = chronological
      ? field === "event_date"
      : sort.field === field;
    const glyph = active
      ? chronological || sort.order === "asc"
        ? "↑"
        : "↓"
      : "↑";
    return (
      <span
        aria-hidden="true"
        className={active ? styles.sortArrowActive : styles.sortArrow}
      >
        {glyph}
      </span>
    );
  };

  const toggleSort = (field: SortField): void => {
    const next = nextSort(sort, field);
    setFilter({ sort: next.field, order: next.order });
  };

  const sortValue = chronological ? "upcoming" : `${sort.field}:${sort.order}`;

  const hasDateFilter = dateFrom !== "" || dateTo !== "";

  return (
    <div className={page.page}>
      <div className={page.content}>
        <AdminPageHeader
          title="Events"
          subtitle={
            loading || total === 0
              ? "Manage events"
              : `Showing ${startItem}-${endItem} of ${total} events`
          }
        />

        {error && (
          <Banner
            variant="error"
            className={styles.banner}
            onDismiss={() => setError(null)}
          >
            {error}
          </Banner>
        )}

        {/* ─── Filters ──────────────────────────────────────────────────── */}
        <div className={styles.filters}>
          <div className={styles.filterRow}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events, clients, quotes..."
              aria-label="Search events"
              className={cx(styles.filterField, styles.search)}
            />
            <div className={cx(styles.picker, styles.pickerPhase)}>
              <Select
                size="sm"
                value={phaseFilter}
                onChange={(next) => {
                  if (isPhaseFilter(next)) setFilter({ phase: next });
                }}
                ariaLabel="Filter by phase"
                options={[
                  { value: "", label: "All Phases" },
                  ...PHASE_ORDER.map((p) => ({
                    value: p,
                    label: PHASE_LABELS[p],
                  })),
                  { value: "cancelled", label: PHASE_LABELS.cancelled },
                ]}
              />
            </div>
            <div className={styles.picker}>
              <Select
                size="sm"
                value={clientFilter}
                onChange={(next) => setFilter({ client: next })}
                ariaLabel="Filter by client"
                // The filter matches on name, so two contacts sharing one
                // client name are a single option (they were two identical
                // rows in the native list).
                options={[
                  { value: "", label: "All Clients" },
                  ...Array.from(new Set(clients.map((c) => c.client_name))).map(
                    (name) => ({ value: name, label: name }),
                  ),
                ]}
              />
            </div>
            {/* Full-width on mobile so the two date inputs share the row
                and shrink instead of spilling past the card. */}
            <div className={styles.dateRange}>
              <DateInputNZ
                value={dateFrom}
                onChange={(next) => setFilter({ from: next })}
                ariaLabel="Filter events from date"
                inputStyles={cx(styles.filterField, styles.dateField)}
              />
              <span className={styles.dateSeparator}>to</span>
              <DateInputNZ
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(next) => setFilter({ to: next })}
                ariaLabel="Filter events to date"
                inputStyles={cx(styles.filterField, styles.dateField)}
              />
            </div>
            {hasDateFilter && (
              <Button
                variant="link"
                onClick={() => setFilter({ from: "", to: "" })}
              >
                Clear dates
              </Button>
            )}
            {/* Orders whatever the filters above left behind; it narrows
                nothing itself, so it sits last in the row. */}
            <div className={styles.picker}>
              <Select
                size="sm"
                value={sortValue}
                onChange={(next) => {
                  const [field, order = ""] = next.split(":");
                  if (isSortField(field)) setFilter({ sort: field, order });
                }}
                ariaLabel="Sort events"
                options={SORT_OPTIONS}
              />
            </div>
            {selectedIds.size > 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setDeleteError(null);
                  setDeleteModalOpen(true);
                }}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : `Delete (${selectedIds.size})`}
              </Button>
            )}
          </div>
        </div>

        {/* ─── Table ────────────────────────────────────────────────────── */}
        <div>
          {loading ? (
            <div className={styles.loading}>Loading events…</div>
          ) : bookings.length === 0 ? (
            <div className={styles.empty}>No events found</div>
          ) : (
            // scroll="x-auto" is the DESIGN.md wide-table exception: rows are
            // plain <Link>s with no in-cell dropdown, so the wide grid may
            // scroll horizontally at ≥md instead of card-stacking.
            <ResponsiveTable breakpoint="md" scroll="x-auto">
              <TableHead>
                {/* Empty header keeps the desktop checkbox column aligned;
                    the per-page select-all was removed (per-row select +
                    Delete (N) cover bulk-delete at every width). */}
                <TableHeaderCell padding="list" width="10" />
                <TableHeaderCell padding="list">Event</TableHeaderCell>
                <TableHeaderCell
                  padding="list"
                  className={cx(styles.sortable, styles.nowrap)}
                  onClick={() => toggleSort("event_date")}
                >
                  Date{sortIndicator("event_date")}
                </TableHeaderCell>
                <TableHeaderCell
                  padding="list"
                  className={styles.sortable}
                  onClick={() => toggleSort("client_name")}
                >
                  Client{sortIndicator("client_name")}
                </TableHeaderCell>
                <TableHeaderCell padding="list">
                  <Tooltip content="Where a Booking sits in its lifecycle, derived from its dates, approval, and payments.">
                    Phase
                  </Tooltip>
                </TableHeaderCell>
                <TableHeaderCell padding="list">Quote</TableHeaderCell>
              </TableHead>
              <TableBody>
                {bookings.map((booking) => (
                  <EventRow
                    key={booking.id}
                    booking={booking}
                    selected={selectedIds.has(booking.id)}
                    onToggle={toggleRow}
                  />
                ))}
              </TableBody>
            </ResponsiveTable>
          )}
        </div>

        {/* ─── Pagination ───────────────────────────────────────────────── */}
        {!loading && totalPages > 1 && (
          <div className={styles.pager}>
            <button
              type="button"
              aria-label="Previous"
              onClick={() =>
                setFilter({ page: String(Math.max(1, currentPage - 1)) })
              }
              disabled={currentPage <= 1}
              className={styles.pagerButton}
            >
              <ChevronLeftIcon className={styles.pagerIcon} />
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              aria-label="Next"
              onClick={() =>
                setFilter({
                  page: String(Math.min(totalPages, currentPage + 1)),
                })
              }
              disabled={currentPage >= totalPages}
              className={styles.pagerButton}
            >
              <ChevronRightIcon className={styles.pagerIcon} />
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        open={deleteModalOpen}
        title={`Delete ${selectedIds.size} event${selectedIds.size === 1 ? "" : "s"}?`}
        message={deleteModalMessage}
        warning={deleteModalWarning}
        error={deleteError}
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}

function EventRow({
  booking,
  selected,
  onToggle,
}: {
  booking: Booking;
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  const dateDisplay = booking.event_date
    ? formatDateRange(booking.event_date, booking.event_end_date, "short")
    : "Date not provided";
  const timeDisplay = booking.event_time
    ? formatTimeRange(booking.event_time)
    : "";
  const eventTitle = booking.event_name || "Untitled Event";

  // The desktop table keeps its own cell rhythm (px-4 py-4 on the links), so
  // every cell zeroes the primitive's padding at md and lets the Link pad —
  // the whole cell stays a click target either way.
  return (
    <TableRow columns={4}>
      {/* The select checkbox rides the card's top-right corner below md and
          returns to the first column at md. */}
      <TableCell
        corner
        padding="listAbove"
        width="10Above"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          aria-label={`Select event ${booking.event_name ?? booking.quote_number}`}
          checked={selected}
          onChange={() => onToggle(booking.id)}
          className={styles.rowCheckbox}
        />
      </TableCell>
      {/* cornerGap reserves room for the corner checkbox on the mobile card. */}
      <TableCell label="Event" span={4} padding="none" cornerGap="md">
        <Link to={`/events/${booking.id}`} className={styles.cellLink}>
          <div className={styles.titleRow}>
            <span className={styles.title}>{eventTitle}</span>
            {/* A phone-sourced Booking is built from a call transcript, so
                it reads as more speculative than a submitted form. */}
            {booking.inquiry_path === "phone_call" && (
              <span className={styles.phoneBadge}>Phone</span>
            )}
          </div>
          {booking.location && (
            <div className={styles.sub}>{booking.location}</div>
          )}
          {booking.request_summary && (
            <div className={styles.summary}>{booking.request_summary}</div>
          )}
        </Link>
      </TableCell>
      <TableCell label="Date" padding="none" className={styles.nowrapAbove}>
        <Link to={`/events/${booking.id}`} className={styles.cellLink}>
          <div className={styles.primary}>{dateDisplay}</div>
          {timeDisplay && <div className={styles.sub}>{timeDisplay}</div>}
        </Link>
      </TableCell>
      <TableCell label="Client" padding="none">
        <Link to={`/events/${booking.id}`} className={styles.cellLink}>
          <div className={styles.plain}>{booking.client_name || "—"}</div>
          {booking.client_email && (
            <div className={styles.sub}>{booking.client_email}</div>
          )}
          {!booking.client_email && booking.client_phone && (
            <div className={styles.sub}>{booking.client_phone}</div>
          )}
          {booking.preferred_contact_method && (
            <div className={styles.subCapitalised}>
              Preferred: {booking.preferred_contact_method}
            </div>
          )}
        </Link>
      </TableCell>
      <TableCell label="Phase" padding="none" className={styles.nowrapAbove}>
        <Link to={`/events/${booking.id}`} className={styles.cellLink}>
          <span className={cx(badge.badge, PHASE_COLORS[booking.phase])}>
            {PHASE_LABELS[booking.phase]}
          </span>
        </Link>
      </TableCell>
      <TableCell label="Quote" padding="none" className={styles.nowrapAbove}>
        <Link to={`/events/${booking.id}`} className={styles.cellLink}>
          <div className={styles.plain}>#{booking.quote_number || "—"}</div>
          {booking.quote_total !== null && (
            <span className={styles.money}>
              {formatNZD(booking.quote_total)}
            </span>
          )}
        </Link>
      </TableCell>
    </TableRow>
  );
}
